import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { subscriptionsService } from '../services/subscriptions.service';
import { Check, ArrowRight, Package as PackageIcon, Loader2, ArrowLeft, Target, TrendingUp, Zap, Sparkles } from 'lucide-react';
import { LoadingSkeleton } from './LoadingSkeleton';
import toast from 'react-hot-toast';
import { handleApiError } from '../lib/api-client';

interface PackageSelectionProps {
  onPackageSelected?: (opts?: { forceDashboard?: boolean }) => void;
  /** When provided, show a Back button (e.g. for subscribed users viewing upgrade) */
  onBack?: () => void;
  /** Show packages before sign-in; Select Plan becomes "Sign in to subscribe" */
  isGuestPreview?: boolean;
  /** When guest, called when user clicks Sign in to subscribe */
  onSignInRequired?: () => void;
  /** Current subscription - when provided, highlights current package and shows Upgrade/Downgrade. When expired, shows last subscription details. */
  currentSubscription?: Subscription | null;
  /** When true, subscription has expired - show renewal UI and allow selecting new package */
  isExpired?: boolean;
}

export function PackageSelection({
  onPackageSelected,
  onBack,
  isGuestPreview = false,
  onSignInRequired,
  currentSubscription,
  isExpired = false,
}: PackageSelectionProps) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [selectedPackage, setSelectedPackage] = useState<string | null>(null);

  // Fetch packages (works without auth when endpoint is public)
  const {
    data: packages = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ['packages'],
    queryFn: () => subscriptionsService.getPackages(),
  });

  const currentPackageId =
    currentSubscription?.packageId ?? currentSubscription?.package?.id;
  const hasCurrentSubscription = !!currentSubscription;
  /** When true, user has an active (non-expired) subscription - do not allow subscribing to other packages */
  const hasActiveSubscription = hasCurrentSubscription && !isExpired;

  const subscribeMutation = useMutation({
    mutationFn: (packageId: string) =>
      hasCurrentSubscription
        ? subscriptionsService.changePackage(packageId)
        : subscriptionsService.subscribe(packageId),
    onSuccess: (data) => {
      toast.success(
        hasCurrentSubscription ? 'Package updated successfully!' : 'Subscription activated successfully!',
      );
      if (user?.id && data) {
        queryClient.setQueryData(['subscription', user.id], data);
      }
      onPackageSelected?.();
    },
    onError: async (error) => {
      const message = handleApiError(error);
      const isAlreadyHasSubscription =
        /already has an active subscription|already subscribed to this package/i.test(
          message
        );
      if (isAlreadyHasSubscription && onPackageSelected && user?.id) {
        // Backend says user already has a subscription - our cache may be stale.
        // Fetch current subscription, update cache, then navigate to dashboard.
        // Pass forceDashboard so App shows dashboard even if subscription fetch returns null.
        try {
          const sub = await subscriptionsService.getCurrentSubscription();
          if (sub) {
            queryClient.setQueryData(['subscription', user.id], sub);
          } else {
            queryClient.invalidateQueries({ queryKey: ['subscription', user.id] });
          }
          toast.success('You already have an active subscription. Taking you to your dashboard.');
          onPackageSelected({ forceDashboard: true });
        } catch {
          queryClient.invalidateQueries({ queryKey: ['subscription', user.id] });
          toast.success('Taking you to your dashboard.');
          onPackageSelected({ forceDashboard: true });
        }
      } else {
        toast.error(message);
      }
      setSelectedPackage(null);
    },
  });

  const handleSelectPackage = (packageId: string) => {
    if (isGuestPreview && onSignInRequired) {
      onSignInRequired();
      return;
    }
    setSelectedPackage(packageId);
    subscribeMutation.mutate(packageId);
  };

  const getPackageIcon = (name: string) => {
    if (name.includes('Diabetes')) return <Target className="w-8 h-8" />;
    if (name.includes('Obesity') || name.includes('Weight')) return <TrendingUp className="w-8 h-8" />;
    if (name.includes('PCOD')) return <Sparkles className="w-8 h-8" />;
    if (name.includes('Fatty Liver')) return <Zap className="w-8 h-8" />;
    if (name.includes('Hypertension')) return <TrendingUp className="w-8 h-8" />;
    return <Check className="w-8 h-8" />;
  };

  const getPackageColor = (index: number) => {
    const colors = [
      'from-[#2c5177] to-[#1e3b57]',
      'from-[#72a83f] to-[#5a8632]',
      'from-blue-500 to-blue-600',
      'from-green-500 to-green-600',
    ];
    return colors[index % colors.length];
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 p-6">
        <div className="max-w-6xl mx-auto">
          <div className="mb-8">
            <LoadingSkeleton variant="rectangular" height="3rem" className="mb-4" />
            <LoadingSkeleton variant="text" lines={2} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <LoadingSkeleton key={i} variant="rectangular" height="400px" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 border-2 border-red-200">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Error Loading Packages</h2>
          <p className="text-gray-600 mb-6">{handleApiError(error)}</p>
          <button
            onClick={() => window.location.reload()}
            className="w-full bg-dropsugar-green hover:bg-dropsugar-green-dark text-white font-semibold py-3 rounded-xl transition-all"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 p-6">
      <div className="max-w-6xl mx-auto">
        {(onBack || isGuestPreview) && (
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-gray-600 hover:text-dropsugar-green mb-6 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            {isGuestPreview ? 'Back' : 'Back to Dashboard'}
          </button>
        )}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            {isExpired ? 'Renew Your Subscription' : isGuestPreview ? 'Choose Your Health Goal' : 'Choose Your Plan'}
          </h1>
          <p className="text-xl text-gray-600">
            {isExpired
              ? 'Your subscription has expired. Select a package below to renew and regain full access.'
              : isGuestPreview
                ? 'View our plans below. Sign in to subscribe and get started.'
                : 'Select a package that fits your health monitoring needs'}
          </p>
        </div>

        {isExpired && currentSubscription && (
          <div className="bg-red-50 rounded-2xl p-6 mb-8 border-2 border-red-200">
            <h3 className="text-sm font-semibold text-red-900 mb-3">Your expired subscription</h3>
            <div className="space-y-2 text-red-800">
              {currentSubscription.package?.name && (
                <p><span className="font-medium">Package:</span> {currentSubscription.package.name}</p>
              )}
              {(currentSubscription.trialExpiresAt || currentSubscription.expiresAt) && (
                <p>
                  <span className="font-medium">Expired on:</span>{' '}
                  {new Date((currentSubscription.trialExpiresAt || currentSubscription.expiresAt)!).toLocaleDateString('en-IN', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                  })}
                </p>
              )}
              {currentSubscription.isTrial && (
                <p className="text-sm font-medium">Free Trial</p>
              )}
            </div>
          </div>
        )}

        {packages.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 border-2 border-gray-200 text-center">
            <PackageIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">No Packages Available</h2>
            <p className="text-gray-600 mb-6">
              Please check back later or contact support. You can start with a free trial to explore
              the app.
            </p>
            {!isGuestPreview && onPackageSelected && (
              <button
                type="button"
                onClick={async () => {
                  try {
                    const data = await subscriptionsService.activateFreeTrial();
                    if (user?.id && data) {
                      queryClient.setQueryData(['subscription', user.id], data);
                    }
                    toast.success('Free trial activated!');
                    onPackageSelected();
                  } catch (err) {
                    toast.error(handleApiError(err));
                  }
                }}
                className="px-6 py-3 bg-dropsugar-green hover:bg-dropsugar-green-dark text-white font-semibold rounded-xl"
              >
                Start Free Trial
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {packages.map((pkg, index) => {
              const isSelected = selectedPackage === pkg.id;
              const isProcessing = subscribeMutation.isPending && isSelected;
              const currentPackageId =
                currentSubscription?.packageId ?? currentSubscription?.package?.id;
              const currentPrice =
                currentSubscription?.package?.price ?? (currentSubscription as any)?.package?.price;
              const isCurrent = !!currentPackageId && currentPackageId === pkg.id;
              const pkgPrice = Number(pkg.price) ?? 0;
              const isUpgrade =
                currentPrice != null && pkgPrice > Number(currentPrice) && !isCurrent;
              const isDowngrade =
                currentPrice != null && pkgPrice < Number(currentPrice) && !isCurrent;

              return (
                <div
                  key={pkg.id}
                  className={`bg-white rounded-2xl p-8 border-2 transition-all relative ${
                    isSelected
                      ? 'border-dropsugar-green shadow-xl scale-105'
                      : isCurrent
                        ? 'border-green-500 shadow-lg ring-4 ring-green-500/20'
                        : 'border-gray-200 hover:border-blue-300 hover:shadow-lg'
                  }`}
                >
                  {isCurrent && (
                    <div className="absolute top-4 right-4 px-4 py-1.5 bg-green-500 text-white rounded-full text-sm font-bold shadow-md flex items-center gap-1.5">
                      <Check className="w-4 h-4" />
                      Current Plan
                    </div>
                  )}
                  <div className={`inline-flex items-center justify-center w-16 h-16 rounded-xl bg-gradient-to-br ${getPackageColor(index)} text-white mb-4 shadow-md`}>
                    {getPackageIcon(pkg.name)}
                  </div>
                  <div className="text-center mb-6">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{pkg.name}</h3>
                    {pkg.description && (
                      <p className="text-gray-600 text-sm mb-4">{pkg.description}</p>
                    )}
                    <div className="flex items-baseline justify-center">
                      <span className="text-4xl font-bold text-gray-900">
                        ₹{Number(pkg.price).toLocaleString('en-IN')}
                      </span>
                      <span className="text-gray-600 ml-2">/ {pkg.durationDays} days</span>
                    </div>
                  </div>

                  {Array.isArray(pkg.features) && pkg.features.length > 0 && (
                    <ul className="space-y-3 mb-8">
                      {pkg.features.map((feature, index) => (
                        <li key={index} className="flex items-start">
                          <Check className="w-5 h-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  )}

                  <button
                    onClick={() =>
                      !isCurrent &&
                      !hasActiveSubscription &&
                      handleSelectPackage(pkg.id)
                    }
                    disabled={
                      isCurrent ||
                      hasActiveSubscription ||
                      (!isGuestPreview &&
                        (subscribeMutation.isPending || isProcessing))
                    }
                    className={`w-full py-3 px-6 rounded-xl font-semibold transition-all flex items-center justify-center space-x-2 ${
                      isCurrent
                        ? 'bg-gray-200 text-gray-600 cursor-default'
                        : hasActiveSubscription
                          ? 'bg-gray-200 text-gray-600 cursor-not-allowed'
                          : isGuestPreview
                            ? 'bg-dropsugar-green text-white hover:bg-dropsugar-green-dark'
                            : isSelected
                              ? 'bg-dropsugar-green text-white hover:bg-dropsugar-green-dark'
                              : isUpgrade
                                ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                                : isDowngrade
                                  ? 'bg-amber-600 text-white hover:bg-amber-700'
                                  : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                    } ${!isGuestPreview && (subscribeMutation.isPending || isProcessing) && !isCurrent ? 'opacity-50 cursor-not-allowed' : ''}`}
                    aria-label={
                      isCurrent
                        ? 'Current plan'
                        : hasActiveSubscription
                          ? `Already subscribed - one subscription per user`
                          : isGuestPreview
                            ? `Sign in to subscribe to ${pkg.name}`
                            : `Select ${pkg.name} package`
                    }
                  >
                    {!isGuestPreview && isProcessing && isSelected ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        <span>Processing...</span>
                      </>
                    ) : !isGuestPreview && isSelected && !isCurrent ? (
                      <>
                        <Check className="w-5 h-5" />
                        <span>Selected</span>
                      </>
                    ) : isCurrent ? (
                      <>
                        <Check className="w-5 h-5" />
                        <span>Current Plan</span>
                      </>
                    ) : hasActiveSubscription ? (
                      <>
                        <Check className="w-5 h-5" />
                        <span>Already subscribed</span>
                      </>
                    ) : isGuestPreview ? (
                      <>
                        <span>Sign in to subscribe</span>
                        <ArrowRight className="w-5 h-5" />
                      </>
                    ) : isUpgrade ? (
                      <>
                        <span>Upgrade</span>
                        <ArrowRight className="w-5 h-5" />
                      </>
                    ) : isDowngrade ? (
                      <>
                        <span>Downgrade</span>
                        <ArrowRight className="w-5 h-5" />
                      </>
                    ) : (
                      <>
                        <span>Select Plan</span>
                        <ArrowRight className="w-5 h-5" />
                      </>
                    )}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
